import {Conversation, AgentCall} from '@stripe-internal/conversations-client';
import {PlanCard} from './PlanCard';
import {PaymentLinkCard} from './PaymentLinkCard';
import {ProductCard} from './ProductCard';
import {CustomerCard} from './CustomerCard';
import {ChargeCard} from './ChargeCard';
import {RefundCard} from './RefundCard';
import {PaymentIntentCard} from './PaymentIntentCard';
import {CouponCard} from './CouponCard';
import {InvoiceCard} from './InvoiceCard';

const SINGLE_PAYMENT_LINK_TOOL_NAMES = [
  'update_payment_link',
  'create_payment_link',
  'retrieve_payment_link',
];
const MULTIPLE_PAYMENT_LINK_TOOL_NAMES = ['list_payment_links'];
const SINGLE_PRODUCT_TOOL_NAMES = [
  'update_product',
  'create_product',
  'retrieve_product',
];
const MULTIPLE_PRODUCT_TOOL_NAMES = ['search_products'];
const SINGLE_CUSTOMER_TOOL_NAMES = [
  'update_customer',
  'create_customer',
  'retrieve_customer',
];
const MULTIPLE_CUSTOMER_TOOL_NAMES = ['search_customers'];
const SINGLE_PRICE_TOOL_NAMES = ['update_price', 'create_price'];
const MULTIPLE_PRICE_TOOL_NAMES = ['search_prices'];
const SINGLE_CHARGE_TOOL_NAMES = ['create_charge', 'retrieve_charge'];
const MULTIPLE_CHARGE_TOOL_NAMES = ['search_charges'];
const SINGLE_PAYMENT_INTENT_TOOL_NAMES = [
  'create_payment_intent',
  'retrieve_payment_intent',
  'update_payment_intent',
];
const MULTIPLE_PAYMENT_INTENT_TOOL_NAMES = ['list_payment_intents'];
const SINGLE_REFUND_TOOL_NAMES = [
  'create_refund',
  'retrieve_refund',
  'update_refund',
];
const MULTIPLE_REFUND_TOOL_NAMES = ['list_refunds'];
const SINGLE_COUPON_TOOL_NAMES = [
  'create_coupon',
  'retrieve_coupon',
  'update_coupon',
  'retrieve_promotion_code',
  'create_promotion_code',
  'update_promotion_code',
];
const MULTIPLE_COUPON_TOOL_NAMES = ['list_coupons'];
const SINGLE_INVOICE_TOOL_NAMES = [
  'update_invoice',
  'create_invoice',
  'retrieve_invoice',
];

type AgentCardProps = {
  agentCall: AgentCall;
  conversation: Conversation;
  messageId: string;
  onClick?: () => void;
};

const getCardConfig = (toolName: string, response: any) => {
  let data = response;
  if (Array.isArray(response)) {
    [data] = response;
  } else if (Array.isArray(response?.customers)) {
    [data] = response.customers;
  }

  if (!data) {
    return null;
  }

  const objectType = data.object;
  let type = '';

  const KNOWN_OBJECT_TYPES = [
    'charge',
    'invoice',
    'payment_intent',
    'payment_link',
    'product',
    'price',
    'customer',
    'coupon',
    'refund',
  ];

  if (objectType === 'promotion_code') {
    type = 'coupon';
  } else if (toolName === 'generate_new_or_updated_plan') {
    type = 'plan';
  } else if (KNOWN_OBJECT_TYPES.includes(objectType)) {
    type = objectType;
  } else if (
    SINGLE_INVOICE_TOOL_NAMES.includes(toolName) ||
    toolName === 'search_invoices'
  ) {
    type = 'invoice';
  } else if (
    SINGLE_PAYMENT_LINK_TOOL_NAMES.includes(toolName) ||
    MULTIPLE_PAYMENT_LINK_TOOL_NAMES.includes(toolName)
  ) {
    type = 'payment_link';
  } else if (
    SINGLE_PRODUCT_TOOL_NAMES.includes(toolName) ||
    MULTIPLE_PRODUCT_TOOL_NAMES.includes(toolName)
  ) {
    type = 'product';
  } else if (
    SINGLE_PRICE_TOOL_NAMES.includes(toolName) ||
    MULTIPLE_PRICE_TOOL_NAMES.includes(toolName)
  ) {
    type = 'price';
  } else if (
    SINGLE_CUSTOMER_TOOL_NAMES.includes(toolName) ||
    MULTIPLE_CUSTOMER_TOOL_NAMES.includes(toolName)
  ) {
    type = 'customer';
  } else if (
    SINGLE_CHARGE_TOOL_NAMES.includes(toolName) ||
    MULTIPLE_CHARGE_TOOL_NAMES.includes(toolName)
  ) {
    type = 'charge';
  } else if (
    SINGLE_PAYMENT_INTENT_TOOL_NAMES.includes(toolName) ||
    MULTIPLE_PAYMENT_INTENT_TOOL_NAMES.includes(toolName)
  ) {
    type = 'payment_intent';
  } else if (
    SINGLE_REFUND_TOOL_NAMES.includes(toolName) ||
    MULTIPLE_REFUND_TOOL_NAMES.includes(toolName)
  ) {
    type = 'refund';
  } else if (
    SINGLE_COUPON_TOOL_NAMES.includes(toolName) ||
    MULTIPLE_COUPON_TOOL_NAMES.includes(toolName)
  ) {
    type = 'coupon';
  }

  let normalizedData = data;

  if (type === 'charge' && data.charge_id) {
    normalizedData = {
      charge: {
        id: data.charge_id,
        amount: data.total_amount_in_smallest_currency_unit,
        currency: data.three_letter_iso_currency_code,
        status: data.payment_status,
      },
      customer: data.customer
        ? {
            id: data.customer.customer_id,
            email: data.customer.customer_email_address,
            name: data.customer.customer_full_name_or_business_name,
          }
        : {
            id: data.associated_customer_id_when_not_expanded,
          },
    };
  } else if (type === 'customer' && data.customer_id) {
    normalizedData = {
      customer: {
        id: data.customer_id,
        email: data.customer_email_address,
        name: data.customer_full_name_or_business_name,
        total_spend_amount:
          data.total_spend_amount ||
          data.current_account_balance_in_smallest_currency_unit,
        total_spend_currency:
          data.total_spend_currency ||
          data.default_currency_for_recurring_billing,
      },
    };
  } else if (type === 'invoice' && data.invoice_id) {
    normalizedData = {
      object: 'invoice',
      id: data.invoice_id,
      number: data.invoice_unique_number,
      status: data.current_invoice_status,
      amount_due: data.total_amount_due_in_smallest_currency_unit,
      currency: data.three_letter_iso_currency_code,
      created: data.invoice_created_at_unix_timestamp,
      customer: {
        id: data.associated_customer_id,
        name: data.customer_full_name_at_invoice_time,
      },
    };
  } else if (type === 'payment_intent' && data.payment_intent_id) {
    normalizedData = {
      payment_intent: {
        id: data.payment_intent_id,
        amount: data.amount_intended_to_collect_in_cents,
        currency: data.currency_code,
        status: data.payment_intent_status,
      },
      customer: data.customer
        ? {
            id: data.customer.customer_id,
            email: data.customer.customer_email_address,
            name: data.customer.customer_full_name_or_business_name,
          }
        : {
            id: data.customer_id,
          },
    };
  } else if (type === 'payment_link' && data.payment_link_id) {
    normalizedData = {
      payment_link: {
        id: data.payment_link_id,
        url: data.public_shareable_url_for_payment_link,
      },
      line_items: Array.isArray(data.line_items)
        ? data.line_items.map((item: any) => ({
            price: {
              product: item.price?.product || item.product,
            },
            description: item.description,
            amount_total: item.amount_total,
            currency: item.currency,
          }))
        : data.line_items_being_sold?.data?.map((item: any) => ({
            price: {
              product: item.product_id_for_this_item || item.product,
            },
            description: item.line_item_description,
            amount_total: item.line_item_amount_in_smallest_currency_unit,
            currency: item.three_letter_iso_currency_code,
          })),
    };
  } else if (type === 'product' && data.product_id) {
    normalizedData = {
      product: {
        id: data.product_id,
        name: data.product_name_displayable_to_customers,
        images: data.list_of_image_urls_for_product,
      },
      price: data.price
        ? {
            id: data.price.price_id || data.price.id,
            unit_amount:
              data.price.amount_per_unit_in_smallest_currency_unit ||
              data.price.unit_amount,
            currency:
              data.price.three_letter_iso_currency_code || data.price.currency,
          }
        : undefined,
    };
  } else if (type === 'price' && data.price_id) {
    normalizedData = {
      price: {
        id: data.price_id,
        unit_amount: data.amount_per_unit_in_smallest_currency_unit,
        currency: data.three_letter_iso_currency_code,
      },
      product: {
        id: data.product_id_this_price_belongs_to,
      },
    };
  } else if (type === 'refund' && data.refund_id) {
    normalizedData = {
      refund: {
        id: data.refund_id,
        amount: data.refund_amount_in_smallest_currency_unit,
        currency: data.three_letter_iso_currency_code,
      },
      charge: data.charge
        ? {
            id: data.charge.charge_id,
            amount: data.charge.total_amount_in_smallest_currency_unit,
            currency: data.charge.three_letter_iso_currency_code,
          }
        : {
            id: data.associated_charge_id_when_not_expanded,
          },
      customer: data.customer
        ? {
            id: data.customer.customer_id,
            email: data.customer.customer_email_address,
            name: data.customer.customer_full_name_or_business_name,
          }
        : undefined,
    };
  } else if (
    (type === 'coupon' || type === 'promotion_code') &&
    (data.coupon_id || data.promotion_code_id)
  ) {
    if (data.object === 'coupon') {
      normalizedData = {
        object: 'coupon',
        id: data.coupon_id,
        name: data.coupon_name,
        amount_off: data.amount_off_in_smallest_currency_unit,
        percent_off: data.percent_off_decimal,
        currency: data.three_letter_iso_currency_code,
        redeem_by: data.redeem_by_unix_timestamp,
      };
    } else {
      normalizedData = {
        object: 'promotion_code',
        id: data.promotion_code_id,
        code: data.promotion_code_text,
        coupon: {
          id: data.coupon_id,
          name: data.coupon_name,
          amount_off: data.amount_off_in_smallest_currency_unit,
          percent_off: data.percent_off_decimal,
          currency: data.three_letter_iso_currency_code,
        },
      };
    }
  }

  return {type, data: normalizedData};
};

export const AgentCard = ({
  agentCall,
  conversation,
  messageId,
  onClick,
}: AgentCardProps) => {
  const {tool_name: toolName, response} = agentCall;

  const config = getCardConfig(toolName, response);

  if (!config) {
    return null;
  }

  const {type, data} = config;

  switch (type) {
    case 'plan':
      return (
        <PlanCard
          agentCallResponse={response}
          conversation={conversation}
          messageId={messageId}
        />
      );
    case 'invoice':
      return <InvoiceCard agentCallResponse={data} onClick={onClick} />;
    case 'payment_link':
      return <PaymentLinkCard agentCallResponse={data} onClick={onClick} />;
    case 'product':
    case 'price':
      return <ProductCard agentCallResponse={data} onClick={onClick} />;
    case 'charge':
      return <ChargeCard agentCallResponse={data} onClick={onClick} />;
    case 'payment_intent':
      return <PaymentIntentCard agentCallResponse={data} onClick={onClick} />;
    case 'refund':
      return <RefundCard agentCallResponse={data} onClick={onClick} />;
    case 'customer':
      return <CustomerCard agentCallResponse={data} onClick={onClick} />;
    case 'coupon':
      return <CouponCard agentCallResponse={data} onClick={onClick} />;
    default:
      return null;
  }
};
